import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { WorkspaceBundle } from '../supabase/repository'
import { loadWorkspaceBundle } from '../supabase/repository'
import { supabase } from '../supabase/client'

interface WorkspaceContextValue { bundle: WorkspaceBundle | null; loading: boolean; error: string; reload(): Promise<void>; setBundle: React.Dispatch<React.SetStateAction<WorkspaceBundle | null>> }
const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [bundle, setBundle] = useState<WorkspaceBundle | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const initialized = useRef(false)
  const reload = useCallback(async () => { if(!initialized.current)setLoading(true); setError(''); try { setBundle(await loadWorkspaceBundle()); initialized.current=true } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not load workspace.') } finally { setLoading(false) } }, [])
  useEffect(() => { void reload() }, [reload])
  useEffect(() => {
    if (!supabase || !bundle) return
    const realtime = supabase
    const channel = realtime.channel(`workspace:${bundle.workspace.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'npcs', filter: `workspace_id=eq.${bundle.workspace.id}` }, () => void reload())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'seasons', filter: `workspace_id=eq.${bundle.workspace.id}` }, () => void reload())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'locations', filter: `workspace_id=eq.${bundle.workspace.id}` }, () => void reload()).subscribe()
    return () => { void realtime.removeChannel(channel) }
  }, [bundle, reload])
  const value = useMemo(() => ({ bundle, loading, error, reload, setBundle }), [bundle, loading, error, reload])
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

export function useWorkspace() { const value = useContext(WorkspaceContext); if (!value) throw new Error('useWorkspace must be used inside WorkspaceProvider'); return value }
